/**
 * API utility with automatic 401 and 429 handling
 * Automatically logs out user when token is invalid/expired
 * Triggers upgrade modal when rate limit is hit
 */

import { useAuthStore } from '../stores/authStore'
import { RateLimitError } from '../types/tier'

// Event for rate limit errors - components can listen to this
export const RATE_LIMIT_EVENT = 'rate-limit-exceeded'

export interface RateLimitEventDetail {
  error: RateLimitError
}

/**
 * Dispatch rate limit event for global handling
 */
export function dispatchRateLimitError(error: RateLimitError) {
  window.dispatchEvent(new CustomEvent<RateLimitEventDetail>(RATE_LIMIT_EVENT, {
    detail: { error }
  }))
}

export async function apiFetch(url: string, options: RequestInit = {}) {
  const response = await fetch(url, options)
  
  // If unauthorized, clear auth and redirect to login
  if (response.status === 401) {
    console.warn('🔒 Unauthorized (401) - Token expired or invalid, logging out...')
    
    // Get logout function from store
    const { logout } = useAuthStore.getState()
    logout()
    
    // Redirect to login
    window.location.href = '/login'
    
    throw new Error('Unauthorized - Please login again')
  }
  
  // If rate limited, dispatch event for upgrade modal
  if (response.status === 429) {
    console.warn('⚠️ Rate limit exceeded (429)')
    
    try {
      const errorData = await response.clone().json()
      dispatchRateLimitError(errorData as RateLimitError)
    } catch {
      // If we can't parse the error, dispatch a generic one
      dispatchRateLimitError({
        error: 'rate_limit_exceeded',
        message: 'You have reached your usage limit',
        usage: { current: 0, limit: 0, resetTime: '', resetIn: 'soon' },
        tier: 'free'
      })
    }
  }
  
  return response
}

/**
 * Helper to get auth headers
 */
export function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('kuraAuthToken')
  
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  }
}
